import {
  createContext,
  ReactNode,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";

export type TimerState = "idle" | "recording" | "paused";

const STORAGE_KEY = "faye:time-recorder-session";

export interface TimeRecordingSession {
  taskBlockRef: string;
  taskRef: string;
  taskTitle: string;
  startTime: number;
  elapsedMs: number;
  description: string;
  state: TimerState;
}

interface TimeRecorderContextType {
  session: TimeRecordingSession | null;
  startRecording: (
    taskBlockRef: string,
    taskRef: string,
    taskTitle: string,
  ) => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  cancelRecording: () => void;
  updateDescription: (description: string) => void;
  saveRecording: (
    onSave: (entry: {
      hoursReported: number;
      minutesReported: number;
      description: string;
      date: number;
      dateYmd: string;
    }) => void,
  ) => void;
}

export const TimeRecorderContext = createContext<TimeRecorderContextType>({
  session: null,
  startRecording: () => {},
  pauseRecording: () => {},
  resumeRecording: () => {},
  cancelRecording: () => {},
  updateDescription: () => {},
  saveRecording: () => {},
});

function loadSession(): TimeRecordingSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as TimeRecordingSession;
  } catch {
    return null;
  }
}

function persistSession(session: TimeRecordingSession | null) {
  if (session) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export const TimeRecorderContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [session, setSession] = useState<TimeRecordingSession | null>(
    loadSession,
  );
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimerInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startTimerInterval = useCallback(() => {
    clearTimerInterval();
    intervalRef.current = setInterval(() => {
      setSession((prev) => {
        if (!prev || prev.state !== "recording") return prev;
        return {
          ...prev,
          elapsedMs: Date.now() - prev.startTime,
        };
      });
    }, 1000);
  }, [clearTimerInterval]);

  // Persist session to localStorage on every change
  useEffect(() => {
    persistSession(session);
  }, [session]);

  // On mount, restart the interval if the restored session was recording
  const startTimerIntervalRef = useRef(startTimerInterval);
  startTimerIntervalRef.current = startTimerInterval;
  useEffect(() => {
    const restored = loadSession();
    if (restored?.state === "recording") {
      startTimerIntervalRef.current();
    }
  }, []);

  // Sync across tabs via storage event
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      if (e.newValue) {
        try {
          const updated = JSON.parse(e.newValue) as TimeRecordingSession;
          setSession(updated);
          if (updated.state === "recording") {
            startTimerInterval();
          } else {
            clearTimerInterval();
          }
        } catch {
          // ignore malformed
        }
      } else {
        clearTimerInterval();
        setSession(null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [startTimerInterval, clearTimerInterval]);

  const startRecording = useCallback(
    (taskBlockRef: string, taskRef: string, taskTitle: string) => {
      const now = Date.now();
      setSession({
        taskBlockRef,
        taskRef,
        taskTitle,
        startTime: now,
        elapsedMs: 0,
        description: "",
        state: "recording",
      });
      startTimerInterval();
    },
    [startTimerInterval],
  );

  const pauseRecording = useCallback(() => {
    clearTimerInterval();
    setSession((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        elapsedMs: Date.now() - prev.startTime,
        state: "paused",
      };
    });
  }, [clearTimerInterval]);

  const resumeRecording = useCallback(() => {
    setSession((prev) => {
      if (!prev) return null;
      const now = Date.now();
      return {
        ...prev,
        startTime: now - prev.elapsedMs,
        state: "recording",
      };
    });
    startTimerInterval();
  }, [startTimerInterval]);

  const cancelRecording = useCallback(() => {
    clearTimerInterval();
    setSession(null);
  }, [clearTimerInterval]);

  const updateDescription = useCallback((description: string) => {
    setSession((prev) => {
      if (!prev) return null;
      return { ...prev, description };
    });
  }, []);

  const saveRecording = useCallback(
    (
      onSave: (entry: {
        hoursReported: number;
        minutesReported: number;
        description: string;
        date: number;
        dateYmd: string;
      }) => void,
    ) => {
      if (!session) return;

      clearTimerInterval();
      const finalElapsedMs =
        session.state === "recording"
          ? Date.now() - session.startTime
          : session.elapsedMs;
      const totalMinutes = Math.round(finalElapsedMs / 60000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const now = new Date();
      const dateYmd = `${now.getFullYear()}-${String(
        now.getMonth() + 1,
      ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

      onSave({
        hoursReported: hours,
        minutesReported: minutes,
        description: session.description,
        date: Date.now(),
        dateYmd,
      });

      setSession(null);
    },
    [session, clearTimerInterval],
  );

  return (
    <TimeRecorderContext.Provider
      value={{
        session,
        startRecording,
        pauseRecording,
        resumeRecording,
        cancelRecording,
        updateDescription,
        saveRecording,
      }}
    >
      {children}
    </TimeRecorderContext.Provider>
  );
};
